<template>
	<li
		class="properties__item properties-item"
		:data-visibility-score="property.ads[0].ad.visibilityScore"
	>
		<a :href="getPropertyUrl" class="properties-item__content" target="_blank">
			<header class="properties-item__header">
				<figure class="properties-item__figure">
					<img
						:src="getPropertyImage"
						:alt="property.titleAutogenerated"
						class="properties-item__image"
					/>
				</figure>
			</header>
			<div class="properties-item__main">
				<div class="properties-item__copy properties-item-copy">
					<h3 class="properties-item-copy__title">{{ propertyTitle }}</h3>
					<p class="properties-item-copy__description">
						{{ property.descriptionShortened }}
					</p>
					<span class="properties-item-copy__contract">{{ getContractType }}</span>
				</div>
				<div class="properties-item__details properties-item-details">
					<ul class="properties-item-details__list">
						<li
							class="properties-item-details__item properties-item-details__item--location"
						>
							<span class="properties-item-details__icon">place</span>
							<span
								class="properties-item-details__text properties-item-details__text--location"
							>
								{{ property.locationGenericFormatted }}
							</span>
						</li>
						<li
							class="properties-item-details__item properties-item-details__item--price"
						>
							<span
								class="properties-item-details__text properties-item-details__text--price"
								v-html="getFormattedPrice"
							></span>
						</li>
						<li
							class="properties-item-details__item properties-item-details__item--extradetails"
						>
							<div
								class="properties-item-details__item properties-item-details__item--surfacearea"
								v-if="property.surfaceArea"
							>
								<span class="properties-item-details__icon">tab_unselected</span>
								<span class="properties-item-details__text">
									{{ property.surfaceArea }} m²
								</span>
							</div>
							<div
								class="properties-item-details__item properties-item-details__item--bedrooms"
								v-if="property.bedrooms"
							>
								<span class="properties-item-details__icon">hotel</span>
								<span class="properties-item-details__text">
									{{ property.bedrooms }}
								</span>
							</div>
							<div
								class="properties-item-details__item properties-item-details__item--bathrooms"
								v-if="property.bathrooms"
							>
								<span class="properties-item-details__icon">bathtub</span>
								<span class="properties-item-details__text">
									{{ property.bathrooms }}
								</span>
							</div>
						</li>
					</ul>
				</div>
			</div>
			<footer class="properties-item__footer">
				<div class="properties-item__agency properties-item-agency">
					<img
						:src="property.advertiser.logoUrl"
						:alt="`${property.advertiser.name} Logo`"
						class="properties-item-agency__logo"
					/>
				</div>
				<div class="properties-item__view-button properties-item-view-button">
					<span class="properties-item-view-button__text">Scopri</span>
					<span class="properties-item-view-button__icon">keyboard_arrow_right</span>
				</div>
			</footer>
		</a>
	</li>
</template>

<script setup>
import {computed} from 'vue'
import {
	formatContractType,
	formatPropertyPrice,
	convertPropertyImageUrl,
} from '~/default/vue/realestate/realestate'

const {property} = defineProps({
	property: {
		type: Object,
		required: true,
	},
})

const getPropertyUrl = computed(() => {
	return `https://case.asiago.it/it/immobile/${property.asiagoItReferenceCode.toLowerCase()}/#main`
})

const getContractType = formatContractType(property.contracts)
const getFormattedPrice = formatPropertyPrice(property.lowestPriceFormatted)

const propertyTitle = computed(() => {
	return property.title || property.titleAutogenerated
})

const getPropertyImage = computed(() => {
	const originalUrl = property.images.find(x => x.cover).types.find(x => x.name === 'thumb').url

	return convertPropertyImageUrl(originalUrl)
})
</script>

<style lang="scss" scoped>
// Simple Asiago.it styles reset
* {
	box-sizing: border-box;
	margin: 0;
	padding: 0;
	display: block;
}
ul {
	list-style-type: none;
}
a {
	text-decoration: none;
	color: inherit;
	&:hover {
		opacity: 1;
	}
}

.properties-item {
	position: relative;
	padding: 0.5rem;
	width: 50%;
	margin: 0;
	@media not all and (min-width: 30em) {
		width: 100%;
	}
	.properties-item__content {
		position: relative;
		width: 100%;
		height: 100%;
		background: #fff;
		color: #333;
		display: flex;
		flex-flow: column nowrap;
		align-items: stretch;
		justify-content: center;
		transition: all 0.3s;
		box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.15);
		&:hover {
			box-shadow: 0 0 1rem rgba(0, 0, 0, 0.25);
			.properties-item__image {
				transform: scale(1.05);
			}
		}
	}
	.properties-item__header {
		width: 100%;
		position: relative;
		overflow: hidden;
		&::before {
			font-weight: 700;
			text-transform: uppercase;
			line-height: 1;
			position: absolute;
			top: 0.5rem;
			left: 0;
			z-index: 1;
			padding: 3px 6px 2px;
			@media not all and (min-width: 60em) {
				font-size: 10px;
			}
			@media (min-width: 60em) {
				font-size: 12px;
			}
		}
		.properties-item__figure {
			position: relative;
			width: 100%;
			height: 0;
			padding-bottom: calc(3 / 4 * 100%);
			overflow: hidden;
			.properties-item__image {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				object-fit: cover;
				transition: all 1s;
				margin: 0;
			}
		}
	}
	.properties-item__main {
		padding: 0.5rem;
		flex-grow: 1;
		display: flex;
		flex-flow: column nowrap;
		align-items: flex-start;
		justify-content: flex-start;
		width: 100%;
		.properties-item-copy {
			flex-grow: 1;
			display: flex;
			flex-flow: column nowrap;
			align-items: flex-start;
			justify-content: flex-start;
			width: 100%;
			&__title {
				font-size: 14px;
				font-weight: 600;
				margin-bottom: 0.5rem;
				line-height: 1.2;
			}
			&__description {
				font-weight: 400;
				line-height: 1.3;
				flex-grow: 1;
				margin-top: 0 !important;
				margin-bottom: 0.75rem !important;
				text-align: left;
				font-size: 12px;
			}
			&__contract {
				line-height: 1.3;
				text-align: right;
				text-transform: uppercase;
				font-size: 12px;
				width: 100%;
				font-weight: 600;
			}
		}
		.properties-item-details {
			width: 100%;
			.properties-item-details__list {
				display: grid;
				grid-template-columns: 1fr 105px;
				grid-template-areas: 'location price' 'extradetails price';
				grid-gap: 0.5rem;
				width: 100%;
				margin: 0;
				.properties-item-details__item {
					display: flex;
					flex-flow: row nowrap;
					align-items: center;
					justify-content: flex-start;
					margin: 0;
					.properties-item-details__icon {
						font-family: 'Material Icons';
						white-space: nowrap;
						word-wrap: normal;
						-moz-osx-font-smoothing: grayscale;
						-moz-font-feature-settings: 'liga';
						-webkit-font-feature-settings: 'liga';
						font-feature-settings: 'liga';
						user-select: none;
						text-transform: lowercase;
						font-size: 24px;
						color: #1b77bf;
						margin-right: 0.25rem;
						line-height: 1;
					}
					.properties-item-details__text {
						font-weight: 400;
						font-size: 13px;
						line-height: 1;
						color: inherit;
						&.properties-item-details__text--location {
							font-weight: 600;
						}
						&.properties-item-details__text--price {
							strong {
								font-weight: 700;
								line-height: inherit;
								font-size: 15px;
								color: inherit;
								display: inline-block;
								small {
									display: inline;
									font-weight: 700;
									font-size: 12px;
								}
							}
						}
					}
					&--location {
						grid-area: location;
					}
					&--extradetails {
						grid-area: extradetails;
						display: flex;
						flex-flow: row wrap;
						align-items: center;
						justify-content: flex-start;
						.properties-item-details__item {
							&:not(:last-child) {
								margin-right: 1rem;
							}
						}
					}
					&--price {
						grid-area: price;
						align-self: center;
						justify-self: flex-end;
						.properties-item-details__text {
							text-align: right;
							line-height: 1.3;
						}
						.properties-item-details__icon {
							align-self: start;
							padding-top: 2px;
						}
					}
				}
			}
		}
	}
	.properties-item__footer {
		padding: 0.5rem;
		padding-top: 0;
		display: grid;
		grid-template-columns: 2fr auto;
		grid-template-areas: 'agency discover';
		grid-gap: 1rem;
		width: 100%;
		.properties-item-agency {
			grid-area: agency;
			width: 100%;
			height: 35px;
			align-self: end;
			justify-self: start;
			&__logo {
				width: 100%;
				height: 100%;
				object-fit: contain;
				object-position: 0%;
				margin: 0;
			}
		}
		.properties-item-view-button {
			grid-area: discover;
			justify-self: end;
			display: flex;
			background: #339933;
			border: 1px solid darken(#339933, 10%);
			flex-flow: row nowrap;
			align-items: center;
			justify-content: center;
			padding: 0.5rem 1rem;
			transition: all 0.3s;
			border-radius: 0.25rem;
			&:hover {
				filter: brightness(1.1);
			}
			&__text {
				font-weight: 700;
				text-transform: uppercase;
				line-height: 1;
				@media not all and (min-width: 60em) {
					font-size: 0.875rem;
				}
				@media (min-width: 60em) {
					font-size: 1rem;
				}
				color: #fff;
				text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
			}
			&__icon {
				display: none;
				font-family: 'Material Icons';
				white-space: nowrap;
				word-wrap: normal;
				-moz-osx-font-smoothing: grayscale;
				-moz-font-feature-settings: 'liga';
				-webkit-font-feature-settings: 'liga';
				font-feature-settings: 'liga';
				user-select: none;
				text-transform: lowercase;
				font-size: 24px;
				line-height: 1;
				color: #fff;
				text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
			}
		}
	}
}

/* PLATINUM STYLES (SCORE 400) */
.properties-item[data-visibility-score='400'] {
	.properties-item__header {
		&::before {
			content: 'Platinum';
			background: linear-gradient(to bottom, #d5d7e5, #eee, #d5d7e5);
			color: #666;
			text-shadow: 0 0 4px rgba(255, 255, 255, 0.5);
		}
	}
}

/* GOLD STYLES (SCORE 300) */
.properties-item[data-visibility-score='300'] {
	.properties-item__header {
		&::before {
			content: 'Gold';
			background: linear-gradient(to bottom, #c39738, #deb761, #c39738);
			color: #fff;
			text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
		}
	}
}

/* PREMIUM STYLES (SCORE 200) */
.properties-item[data-visibility-score='200'] {
	.properties-item__header {
		&::before {
			content: 'Premium';
			background: #32475b;
			color: #fff;
		}
	}
}

/* BASIC STYLES (SCORE 100) */
.properties-item[data-visibility-score='100'] {
	.properties-item__header {
		&::before {
			display: none;
		}
	}
}
</style>
